import React from "react";
import styled from "styled-components";
import { Alert, Button, Input, Typography } from "antd";
import { device } from "../../lib/breakpoints";

const { Title } = Typography;

/** Render Leo's lightweight markdown (**bold**, newlines) without exposing raw markers. */
export const renderLeoMarkdown = (text) => {
  if (text == null) return null;
  const value = String(text).replace(/\r\n/g, "\n");
  if (!value) return null;

  return value.split("\n").map((line, lineIndex, lines) => (
    <React.Fragment key={`${lineIndex}-${line.slice(0, 24)}`}>
      {line.split(/(\*\*[^*]+\*\*)/g).map((part, partIndex) =>
        part.startsWith("**") && part.endsWith("**") && part.length > 4 ? (
          <strong key={partIndex}>{part.slice(2, -2)}</strong>
        ) : (
          <React.Fragment key={partIndex}>{part}</React.Fragment>
        )
      )}
      {lineIndex < lines.length - 1 ? <br /> : null}
    </React.Fragment>
  ));
};

export const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  max-height: 100vh;
  height: 100dvh;
  max-height: 100dvh;
  min-height: 0;
  width: 100%;
  overflow: hidden;
  box-sizing: border-box;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
`;

export const ChatHeader = styled.div`
  flex-shrink: 0;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid rgba(44, 95, 45, 0.1);
  background: linear-gradient(
    135deg,
    var(--color-primary) 0%,
    var(--color-primary-dark) 100%
  );
  box-shadow: 0 2px 8px rgba(44, 95, 45, 0.15);
`;

export const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  position: relative;
`;

export const LogoImage = styled.img`
  width: 120px;
  height: 120px;
  object-fit: contain;
  filter: drop-shadow(0 4px 12px rgba(44, 95, 45, 0.2));

  &.small {
    width: 32px;
    height: 32px;
    filter: brightness(0) invert(1);
  }

  &.tiny {
    width: 16px;
    height: 16px;
    filter: brightness(0) invert(1);
  }

  &.message {
    width: 16px;
    height: 16px;
    filter: brightness(0) saturate(100%) hue-rotate(120deg) brightness(1.2);
  }
`;

export const HeaderTitle = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 400;
  text-align: center;
  letter-spacing: 0.5px;
`;

export const ParticipantsList = styled.div`
  margin: 0.5rem auto 0;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  width: 100%;
  max-width: 600px;
  box-sizing: border-box;
  display: flex;
  justify-content: center;

  @media ${device.mobile} {
    margin: 0.5rem 0 0;
    padding: 0.375rem 0 0;
    max-width: 100%;
    justify-content: flex-start;
  }
`;

export const ParticipantItem = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
  font-size: 0.85rem;
  width: 100%;
  justify-content: center;

  @media ${device.mobile} {
    gap: 0.5rem;
    font-size: 0.8rem;
    align-items: center;
    padding: 0;
    justify-content: center;
  }
`;

export const ParticipantIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  height: 100%;

  @media ${device.mobile} {
    align-self: center;
  }
`;

export const ParticipantTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  align-items: center;
  flex: 1;
  min-width: 0;
  justify-content: center;

  @media ${device.mobile} {
    gap: 0.25rem;
    width: 100%;
    margin-top: 0;
    padding: 0;
    justify-content: center;
  }
`;

export const ParticipantTag = styled.span`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid rgba(255, 255, 255, 0.3);
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  flex-shrink: 0;

  @media ${device.mobile} {
    padding: 0.2rem 0.4rem;
    font-size: 0.7rem;
    border-radius: 10px;
    line-height: 1.3;
    max-width: 100%;
    min-width: fit-content;

    /* Allow wrapping but prefer keeping name and label together */
    white-space: normal;

    /* Ensure icons don't shrink */
    > svg,
    > .anticon {
      flex-shrink: 0;
    }

    /* Allow text to wrap if needed, but try to keep name and label on same line */
    > span:first-of-type {
      white-space: nowrap;
    }

    > span:last-of-type {
      white-space: nowrap;
    }
  }
`;

export const NotificationSettings = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 1rem;
  display: flex;
  justify-content: center;

  @media ${device.mobile} {
    top: 0.5rem;
    right: 1rem;
  }
`;

export const NotificationButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: white;
  padding: 0.5rem;
  border-radius: 8px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  min-height: 40px;
  width: max-content !important;
  gap: 8px;
  position: absolute;
  right: -20px;
  z-index: 10;

  span {
    color: white;
    font-size: 0.9rem;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.4);
    transform: translateY(-1px);
  }

  &:active {
    transform: scale(0.95);
  }

  .anticon {
    font-size: 16px;
    line-height: 1;
    color: white !important;
  }

  @media ${device.mobile} {
    width: 36px;
    height: 36px;
    padding: 0.4rem;

    span {
      display: none;
    }

    .anticon {
      font-size: 14px;
      color: white !important;
      display: block !important;
      visibility: visible !important;
    }
  }
`;

export const NotificationStatus = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.75rem;
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  min-height: 40px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  position: absolute;
  top: 0px;
  right: -20px;
  width: max-content;
  gap: 8px;

  span {
    color: white;
    font-size: 0.9rem;
  }

  .anticon {
    font-size: 16px;
    line-height: 1;
    color: white !important;
  }

  @media ${device.mobile} {
    width: 36px;
    height: 36px;
    padding: 0.4rem;

    span {
      display: none;
    }

    .anticon {
      font-size: 14px;
      color: white !important;
      display: block !important;
      visibility: visible !important;
    }
  }
`;

export const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem 0;
  max-width: 768px;
  margin: 0 auto;
  width: 100%;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-radius: 0;
  margin-top: 0;
  margin-bottom: 0;
  padding-top: 2rem;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(241, 241, 241, 0.5);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--color-primary);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: var(--color-primary-dark);
  }
`;

export const MessageBubble = styled.div`
  display: flex;
  margin-bottom: 1.5rem;
  align-items: flex-start;
  padding: 0 1rem;
  flex-direction: ${(props) =>
    props.$sender === "tenant" ? "row-reverse" : "row"};

  &:last-child {
    margin-bottom: 0;
  }
`;

export const MessageAvatar = styled.div`
  margin-right: ${(props) => (props.$sender === "tenant" ? "0" : "0.75rem")};
  margin-left: ${(props) => (props.$sender === "tenant" ? "0.75rem" : "0")};
  flex-shrink: 0;

  span span {
    filter: brightness(0) invert(1);
  }
`;

export const MessageContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: ${(props) =>
    props.$sender === "tenant" ? "flex-end" : "flex-start"};
`;

export const MessageHeader = styled.div`
  display: flex;
  justify-content: ${(props) =>
    props.$sender === "tenant" ? "flex-end" : "flex-start"};
  align-items: center;
  margin-bottom: 0.25rem;
  flex-direction: ${(props) =>
    props.$sender === "tenant" ? "row-reverse" : "row"};
  gap: 0.5rem;
`;

export const MessageSender = styled.div`
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
`;

export const MessageTime = styled.div`
  font-size: 0.75rem;
  color: #9ca3af;
`;

export const MessageText = styled.div`
  color: ${(props) => {
    if (props.$sender === "tenant") return "#ffffff";
    if (props.$sender === "landlord") return "#1f2937";
    return "#1f2937";
  }};
  line-height: 1.7;
  word-wrap: break-word;
  white-space: pre-wrap; /* preserve newlines from AI responses */
  overflow-wrap: anywhere;
  padding: 0.875rem 1.125rem;
  border-radius: 12px;
  background: ${(props) => {
    if (props.$sender === "tenant") return "var(--color-primary)";
    if (props.$sender === "landlord") return "rgba(74, 144, 226, 0.08)";
    return "rgba(44, 95, 45, 0.05)";
  }};
  border: ${(props) => {
    if (props.$sender === "tenant") return "1px solid var(--color-primary)";
    if (props.$sender === "landlord")
      return "1px solid rgba(74, 144, 226, 0.25)";
    return "1px solid rgba(44, 95, 45, 0.1)";
  }};
  box-shadow: ${(props) => {
    if (props.$sender === "tenant") return "0 2px 4px rgba(44, 95, 45, 0.2)";
    if (props.$sender === "landlord")
      return "0 2px 4px rgba(74, 144, 226, 0.15)";
    return "0 2px 4px rgba(44, 95, 45, 0.1)";
  }};
  font-size: 0.95rem;
  font-weight: 400;
  text-align: left;
  display: inline-block;
  width: fit-content;
  max-width: 80%;
  align-self: ${(props) =>
    props.$sender === "tenant" ? "flex-end" : "flex-start"};

  @media ${device.mobile} {
    width: fit-content;
    max-width: 95%;
  }
`;

export const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.125rem;
  border-radius: 12px;
  background: rgba(44, 95, 45, 0.05);
  border: 1px solid rgba(44, 95, 45, 0.1);
  box-shadow: 0 2px 4px rgba(44, 95, 45, 0.1);
  font-size: 0.95rem;
  font-weight: 400;
  color: #6b7280;
  font-style: italic;
  width: fit-content;
  max-width: 80%;
`;

export const TypingDots = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--color-primary);
    animation: typingAnimation 1.4s infinite;
    opacity: var(--leo-typing-dot-opacity-1);

    &:nth-child(2) {
      animation-delay: 0.2s;
      opacity: var(--leo-typing-dot-opacity-2);
    }

    &:nth-child(3) {
      animation-delay: 0.4s;
      opacity: var(--leo-typing-dot-opacity-3);
    }
  }

  @keyframes typingAnimation {
    0%,
    60%,
    100% {
      transform: translateY(0);
      opacity: 0.7;
    }
    30% {
      transform: translateY(-8px);
      opacity: 1;
    }
  }
`;

export const InputArea = styled.div`
  flex-shrink: 0;
  padding: 1rem 1rem;
  padding-bottom: max(1rem, env(safe-area-inset-bottom, 0px));
  border-top: 1px solid rgba(44, 95, 45, 0.1);
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.05);
`;

export const InputContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: stretch;
  max-width: 768px;
  margin: 0 auto;

  & > *:nth-child(2) {
    flex: 1;
    min-width: 0;
  }

  .ant-input {
    border: 1px solid #d1d5db !important;
    border-radius: 12px !important;
    padding: 0.875rem 1.125rem !important;
    font-size: 0.95rem !important;
    transition: all 0.2s ease !important;
    background: #ffffff !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05) !important;
    color: #1f2937 !important;
    line-height: 1.5 !important;
    flex: 1;
    min-width: 0;

    &:focus,
    &:hover {
      border-color: var(--color-primary) !important;
      box-shadow: 0 0 0 3px rgba(44, 95, 45, 0.1) !important;
    }

    &::placeholder {
      color: #9ca3af !important;
    }
  }

  @media ${device.mobile} {
    gap: 0.5rem;
    align-items: stretch;
  }
`;

export const SendButton = styled(Button)`
  height: auto !important;
  min-height: 40px;
  border-radius: 8px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  align-self: stretch;
  gap: 0.5rem;

  .ant-btn-icon {
    filter: brightness(0) invert(1);
    margin: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  &:disabled {
    background: #d1d5db !important;
    border-color: #d1d5db !important;
    color: #9ca3af !important;
    cursor: not-allowed !important;
    opacity: 0.6 !important;

    .ant-btn-icon {
      filter: brightness(0) invert(0.5) !important;
    }
  }

  .desktop-text {
    display: inline;

    @media ${device.mobile} {
      display: none;
    }
  }

  @media ${device.mobile} {
    min-width: 40px;
    width: 40px;
    height: auto !important;
    padding: 0 !important;
    margin: 0 !important;

    .anticon {
      margin: 0 !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
    }
  }
`;

export const Disclaimer = styled.div`
  max-width: 768px;
  margin: 0.5rem auto 0;
  text-align: center;
`;

export const DisclaimerText = styled.div`
  font-size: 0.8125rem;
  color: #475569;
  line-height: 1.45;
  font-weight: 400;
  letter-spacing: 0.01em;

  .desktop-text {
    display: block;
    font-size: 0.8125rem;
    max-width: 550px;
    margin: auto;

    @media ${device.mobile} {
      display: none;
    }
  }

  .mobile-text {
    display: none;

    @media ${device.mobile} {
      display: block;
      font-size: 0.8125rem;
      line-height: 1.45;
    }
  }
`;

export const ActionIndicator = styled.div`
  margin-top: 0.5rem;
  display: flex;
  gap: 0.25rem;
`;

export const ActionBadge = styled.span`
  background: rgba(44, 95, 45, 0.1);
  color: var(--color-primary);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
`;

export const LoadingSpinnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  gap: 1rem;
`;

export const LoadingText = styled.div`
  color: #6b7280;
  font-size: 0.9rem;
  font-weight: 500;
`;

/** Full-viewport white screen for chat-unavailable (e.g. landlord subscription inactive) */
export const ChatUnavailablePage = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  overflow: auto;
`;

/** Full-width dark green band at top (matches chat header); white logo */
export const ChatUnavailableLogoBar = styled.div`
  flex-shrink: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem 1.5rem;
  box-sizing: border-box;
  background: linear-gradient(
    135deg,
    var(--color-primary) 0%,
    var(--color-primary-dark) 100%
  );
  box-shadow: 0 2px 8px rgba(44, 95, 45, 0.15);
`;

export const ChatUnavailableMain = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1.25rem 2.5rem;
  gap: 1rem;
  box-sizing: border-box;
  min-height: 0;
`;

export const ChatUnavailableIconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
  color: #faad14;
  font-size: 2.5rem;
`;

export const BlockedTitle = styled(Title)`
  && {
    color: var(--color-text, #111827);
    margin: 0 !important;
    text-align: center;
  }
`;

/** Landlord subscription inactive: notice body only (icon lives above the title, outside this box) */
export const SubscriptionCanceledAlert = styled(Alert)`
  max-width: 520px;
  width: 100%;

  &&.ant-alert-warning {
    background: #fffbe6 !important;
    background-image: none !important;
    border: 1px solid rgba(250, 173, 20, 0.45);
  }

  && .ant-alert-content {
    flex: 1;
    min-width: 0;
  }

  && .ant-alert-description {
    text-align: left;
    color: var(--color-text, #1f2937);
    margin: 0;
  }
`;

// Image-related styled components
export const ImageUploadSection = styled.div`
  margin-bottom: 1rem;
  max-width: 768px;
  margin-left: auto;
  margin-right: auto;
`;

export const InputActions = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
`;

export const AttachButton = styled(Button)`
  color: ${(props) =>
    props.$active ? "var(--color-primary)" : "#000000"} !important;
  border-color: ${(props) =>
    props.$active ? "var(--color-primary)" : "transparent"};
  background: ${(props) =>
    props.$active ? "rgba(44, 95, 45, 0.1)" : "transparent"};

  /* ant-btn-text + globals can wash out icon-only controls; keep paperclip black */
  && .ant-btn-icon,
  && .anticon,
  && .anticon > svg,
  && .anticon svg {
    color: ${(props) =>
      props.$active ? "var(--color-primary)" : "#000000"} !important;
    filter: none !important;
  }

  &:hover:not(:disabled) {
    color: var(--color-primary) !important;
    border-color: var(--color-primary);
    background: rgba(44, 95, 45, 0.1);

    && .ant-btn-icon,
    && .anticon,
    && .anticon > svg,
    && .anticon svg {
      color: var(--color-primary) !important;
    }
  }

  &:disabled {
    color: #d1d5db !important;
    border-color: transparent;
    background: transparent;

    && .ant-btn-icon,
    && .anticon,
    && .anticon > svg,
    && .anticon svg {
      color: #d1d5db !important;
    }
  }
`;

export const MessageImages = styled.div`
  margin-top: 0.75rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.5rem;
  max-width: 300px;
  ${(p) =>
    p.$tenant
      ? "align-self: flex-end; margin-left: auto;"
      : "align-self: flex-start;"}
`;

export const MessageImage = styled.img`
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s ease;
  border: 1px solid rgba(44, 95, 45, 0.2);

  &:hover {
    transform: scale(1.05);
    border-color: var(--color-primary);
  }
`;

/** Magic-link /chat/:slug only (triage, pending uploads) */
export const TriageOuter = styled.div`
  max-width: 768px;
  width: 100%;
  margin: 0 auto;
  padding: 0 1rem;
  flex-shrink: 0;
  box-sizing: border-box;
`;

export const TriageBanner = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  margin: 0.5rem 0 0;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 12px;
  font-size: 0.85rem;
  font-family: var(--font-secondary);
  color: #92400e;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);

  .anticon {
    font-size: 1.1rem;
    flex-shrink: 0;
  }

  span {
    flex: 1;
    min-width: 0;
  }
`;

export const TriageButton = styled(Button)`
  border-radius: 8px !important;
  font-weight: 600 !important;
  font-family: var(--font-secondary) !important;
  font-size: 0.85rem !important;
`;

export const PhoneRow = styled.div`
  display: flex;
  gap: 0.5rem;
  width: 100%;
  margin-top: 0.25rem;
`;

export const PhoneInput = styled(Input)`
  border-radius: 8px !important;
  flex: 1;
`;

export const PublicLeoErrorPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1.25rem;
  gap: 0.75rem;
  min-height: 50vh;
`;

export const PublicLeoErrorTitle = styled.h1`
  font-size: 1.5rem;
  color: var(--color-text);
  margin: 0;
  font-family: var(--font-primary);
`;

export const PublicLeoErrorText = styled.p`
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  text-align: center;
  max-width: 480px;
`;

export const PendingImageSection = styled.div`
  display: flex;
  gap: 0.5rem;
  padding: 0 0 0.75rem;
  flex-wrap: wrap;
  max-width: 768px;
  margin: 0 auto 0.25rem;
`;

export const PendingThumb = styled.div`
  position: relative;
  width: 60px;
  height: 60px;
  border-radius: 8px;
  overflow: visible;
`;

export const PendingImg = styled.img`
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid var(--color-border-light, #e5e7eb);
`;

export const RemoveThumb = styled(Button)`
  position: absolute !important;
  top: -8px !important;
  right: -8px !important;
  width: 20px !important;
  height: 20px !important;
  min-width: 20px !important;
  padding: 0 !important;
  border-radius: 50% !important;
  background: #ef4444 !important;
  border: none !important;
  color: white !important;
  font-size: 10px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);

  .anticon {
    font-size: 9px !important;
  }

  &:hover {
    background: #dc2626 !important;
  }
`;